import { useLocalParticipant } from "@livekit/components-react";
import { useTranslation } from "react-i18next";
import { CallTile } from "./CallTile";
import { SoloEmptyState } from "./SoloEmptyState";
import { useCallLayoutTier } from "../hooks/useCallLayoutTier";
import { useSpeakerStage } from "../hooks/useSpeakerStage";

/**
 * Sidebar layout — main tile + a rail of everyone else. Desktop uses the
 * design's 160px vertical rail. Phones use a tall main tile with a small
 * self/other card stacked at the top-right, matching the attached mock.
 */
export function SidebarLayout() {
  const { t } = useTranslation();
  const tier = useCallLayoutTier();
  const isMobile = tier === "mobile";
  const { mainTrack, thumbnails, isSoloEmpty, speakingSids } = useSpeakerStage();
  const { localParticipant } = useLocalParticipant();

  const main = isSoloEmpty && localParticipant ? (
    <SoloEmptyState participant={localParticipant} />
  ) : mainTrack ? (
    <CallTile
      trackRef={mainTrack}
      isSpeaking={speakingSids.has(mainTrack.participant.sid)}
      size="lg"
      className="h-full w-full"
    />
  ) : (
    <div className="h-full w-full flex items-center justify-center text-sm text-muted-foreground">
      {t("call.speaker.waitingForParticipants")}
    </div>
  );

  if (isMobile) {
    return (
      <div className="h-full flex gap-2.5 p-3">
        <div className="flex-1 min-w-0 min-h-0">{main}</div>
        {thumbnails.length > 0 && (
          <div className="w-[32%] max-w-[140px] shrink-0 flex flex-col gap-2.5 overflow-y-auto no-scrollbar">
            {thumbnails.map((trackRef) => (
              <div
                key={`${trackRef.participant.sid}-${trackRef.source}`}
                className="aspect-video w-full shrink-0"
              >
                <CallTile
                  trackRef={trackRef}
                  isSpeaking={speakingSids.has(trackRef.participant.sid)}
                  size="sm"
                  className="h-full w-full"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex p-4 lg:p-6 gap-3">
      <div className="flex-1 min-h-0">{main}</div>
      {thumbnails.length > 0 && (
        <div className="flex flex-col gap-3 overflow-y-auto no-scrollbar shrink-0" style={{ width: 160 }}>
          {thumbnails.map((trackRef) => (
            <div
              key={`${trackRef.participant.sid}-${trackRef.source}`}
              style={{ height: 110 }}
              className="shrink-0 w-full"
            >
              <CallTile
                trackRef={trackRef}
                isSpeaking={speakingSids.has(trackRef.participant.sid)}
                size="sm"
                className="h-full w-full"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
